/**
 * 읽은 책으로 채우는 별자리.
 *
 * 감정 좌표(book_emotions)를 쓰지 않는다. 좌표가 찍힌 책은 213권 중
 * 160권이고 12권 이상 찍은 사람은 5명뿐이라, 좌표로 모양을 만들면
 * 대부분에게 별이 두세 개인 그림이 된다. 별자리는 모양이 이미 정해져
 * 있고 읽은 책이 그 자리를 채운다 — 완독 권수만 있으면 된다.
 *
 * 두 묶음이다.
 *
 *   탄생 별자리 12개 — 자기 생일의 별자리를 찾게 되는 것이 요점이다.
 *                      1월생부터 12월생까지 달 순서로 늘어놓는다.
 *   그 밖에 6개      — 북두칠성·오리온처럼 모양으로 아는 것들.
 *
 * 별 개수가 그대로 사다리다. 사람별 완독 권수가
 * 50·33·32·18·18·14·11·5·3·3·3·2·2·2·1·1·1·1·1 이어서,
 * 3별부터 시작하면 42%가 지금 하나를 완성할 수 있고 1~2권인 7명은
 * 한두 권만 더 읽으면 첫 별자리가 생긴다.
 *
 * x, y 는 0~1 이다. 그리는 쪽이 캔버스 크기에 맞춰 늘린다.
 * edges 는 stars 의 순번 쌍이다. stars 순서가 별의 번호 순서이므로
 * 선을 따라가는 순서로 적는다.
 */

/** when 이 있으면 탄생 별자리다 */
const C = (name, sub, stars, points, edges, when = null) => ({
  key: name,
  name,
  sub,
  when,
  stars,
  points,
  edges,
  need: stars.length,
});

/* ── 탄생 별자리 · 1월생 → 12월생 ───────────────────────── */
const ZODIAC = [
  C("물병자리", "어깨에 물동이를 진 사람", 
    ["사달수드", "사달멜리크", "감마", "제타", "에타", "세타", "스카트"],
    [[0.10, 0.30], [0.38, 0.20], [0.60, 0.28], [0.70, 0.42], [0.86, 0.32], [0.62, 0.62], [0.48, 0.88]],
    [[0, 1], [1, 2], [2, 3], [3, 4], [3, 5], [5, 6]],
    "1.20 – 2.18"),

  C("물고기자리", "끈으로 묶인 채 서로 다른 곳을 보는 두 마리",
    ["요타", "감마", "오메가", "알레샤", "에타", "타우"],
    [[0.05, 0.20], [0.13, 0.44], [0.30, 0.66], [0.52, 0.88], [0.70, 0.54], [0.88, 0.20]],
    [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5]],
    "2.19 – 3.20"),

  /* 네 점을 고르게 늘어놓으면 그냥 직선으로 보인다. 실제 양자리는
     셰라탄에서 한 번 꺾인다 — 그 꺾임을 살려야 뿔로 읽힌다. */
  C("양자리", "구부러진 뿔 하나로 알아보는 별자리",
    ["메사르팀", "셰라탄", "하말", "보테인"],
    [[0.08, 0.72], [0.28, 0.68], [0.56, 0.40], [0.90, 0.22]],
    [[0, 1], [1, 2], [2, 3]],
    "3.21 – 4.19"),

  /* 히아데스의 V 는 꼭지가 감마다. 한 팔이 델타·엡실론으로 올라가고
     다른 팔이 세타·알데바란으로 내려간다. 두 팔을 좌우로 갈라 놓으면
     지그재그로 보이니, 꼭지를 왼쪽에 두고 둘 다 오른쪽으로 벌린다.
     뿔은 두 팔 끝에서 각각 더 뻗는다. */
  C("황소자리", "붉은 눈 하나와 두 개의 뿔",
    ["엡실론", "델타", "감마", "세타", "알데바란", "엘나스", "알헤카"],
    [[0.42, 0.24], [0.32, 0.44], [0.22, 0.62], [0.44, 0.72], [0.60, 0.64], [0.90, 0.10], [0.90, 0.80]],
    [[0, 1], [1, 2], [2, 3], [3, 4], [0, 5], [4, 6]],
    "4.20 – 5.20"),

  C("쌍둥이자리", "손을 맞잡고 나란히 선 두 사람",
    ["카스토르", "메브수타", "테자트", "프로푸스", "폴룩스", "와사트", "멕부다", "알헤나"],
    [[0.30, 0.08], [0.24, 0.40], [0.12, 0.58], [0.07, 0.74], [0.58, 0.14], [0.52, 0.46], [0.46, 0.70], [0.64, 0.88]],
    [[0, 1], [1, 2], [2, 3], [1, 5], [4, 5], [5, 6], [6, 7]],
    "5.21 – 6.21"),

  C("게자리", "뒤집힌 Y 자로 벌린 집게",
    ["요타", "아셀루스 보레알리스", "아셀루스 아우스트랄리스", "아쿠벤스", "알타르프"],
    [[0.62, 0.10], [0.50, 0.34], [0.54, 0.56], [0.72, 0.84], [0.22, 0.86]],
    [[0, 1], [1, 2], [2, 3], [2, 4]],
    "6.22 – 7.22"),

  C("사자자리", "낫 모양의 갈기와 꼬리 끝의 별",
    ["레굴루스", "에타", "알기에바", "제타", "뮤", "에프실론", "초르트", "조스마", "데네볼라"],
    [[0.28, 0.88], [0.33, 0.72], [0.40, 0.55], [0.33, 0.39], [0.25, 0.25], [0.14, 0.33],
     [0.55, 0.63], [0.67, 0.42], [0.88, 0.28]],
    [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [2, 7], [7, 8], [0, 6], [6, 7]],
    "7.23 – 8.22"),

  C("처녀자리", "밀 이삭을 든 손끝에 스피카가 있다",
    ["자비야바", "포리마", "아우바", "빈데미아트릭스", "스피카", "제타", "요타"],
    [[0.08, 0.34], [0.29, 0.45], [0.40, 0.32], [0.53, 0.16], [0.44, 0.88], [0.63, 0.58], [0.82, 0.74]],
    [[0, 1], [1, 2], [2, 3], [1, 4], [4, 5], [5, 6]],
    "8.23 – 9.22"),

  C("천칭자리", "밤과 낮의 길이를 재던 저울",
    ["주벤에스카말리", "주벤엘하크라비", "주벤엘게누비", "브라키움"],
    [[0.44, 0.18], [0.80, 0.34], [0.28, 0.56], [0.10, 0.84]],
    [[0, 1], [0, 2], [2, 1], [2, 3]],
    "9.23 – 10.22"),

  C("전갈자리", "붉은 심장과 굽은 꼬리를 가진 전갈",
    ["그라파이어스", "디스키다", "안타레스", "타우", "엡실론", "무", "제타", "이오타", "샤울라"],
    [[0.20, 0.16], [0.32, 0.20], [0.46, 0.30], [0.56, 0.40], [0.58, 0.54], [0.52, 0.66],
     [0.62, 0.78], [0.80, 0.80], [0.88, 0.62]],
    [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8]],
    "10.23 – 11.21"),

  C("사수자리", "활을 당긴 반인반마, 주전자를 닮았다",
    ["감마", "카우스 아우스트랄리스", "카우스 메디아", "카우스 보레알리스", "파이", "눙키", "타우", "아스켈라"],
    [[0.12, 0.86], [0.22, 0.66], [0.32, 0.45], [0.40, 0.22], [0.56, 0.36], [0.66, 0.24], [0.80, 0.36], [0.60, 0.58]],
    [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 4]],
    "11.22 – 12.21"),

  C("염소자리", "바다로 이어진 꼬리를 가진 산양",
    ["알게디", "다비", "프사이", "오메가", "감마", "데네브 알게디"],
    [[0.08, 0.20], [0.15, 0.34], [0.30, 0.54], [0.44, 0.70], [0.76, 0.40], [0.90, 0.24]],
    [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 0]],
    "12.22 – 1.19"),
];

/* ── 그 밖에 ─────────────────────────────────────────────── */
const OTHERS = [
  C("여름철 대삼각형", "여름 밤 가장 먼저 눈에 드는 세 별",
    ["베가", "데네브", "알타이르"],
    [[0.50, 0.14], [0.14, 0.60], [0.88, 0.66]],
    [[0, 1], [1, 2], [2, 0]]),

  C("남십자성", "남쪽 바다에서 길을 찾던 십자",
    ["아크룩스", "가크룩스", "베크룩스", "델타"],
    [[0.50, 0.08], [0.50, 0.84], [0.18, 0.46], [0.82, 0.43]],
    [[0, 1], [2, 3]]),

  C("카시오페이아", "북쪽 하늘에 W 로 걸린 왕비",
    ["카프", "쉐다르", "감마", "루크바", "세긴"],
    [[0.08, 0.28], [0.29, 0.62], [0.50, 0.24], [0.72, 0.64], [0.93, 0.26]],
    [[0, 1], [1, 2], [2, 3], [3, 4]]),

  C("백조자리", "은하수를 가로질러 날아가는 새",
    ["데네브", "사드르", "기에나흐", "델타", "알비레오"],
    [[0.50, 0.08], [0.50, 0.48], [0.86, 0.40], [0.14, 0.38], [0.50, 0.90]],
    [[0, 1], [1, 4], [3, 1], [1, 2]]),

  C("북두칠성", "국자 모양으로 북극성을 가리키는 일곱 별",
    ["두베", "메라크", "페크다", "메그레즈", "알리오트", "미자르", "알카이드"],
    [[0.82, 0.20], [0.80, 0.50], [0.61, 0.57], [0.61, 0.30], [0.42, 0.26], [0.24, 0.17], [0.06, 0.27]],
    [[0, 1], [1, 2], [2, 3], [3, 0], [3, 4], [4, 5], [5, 6]]),

  C("오리온", "겨울 하늘의 사냥꾼, 허리에 세 별을 찼다",
    ["베텔게우스", "벨라트릭스", "알니탁", "알닐람", "민타카", "사이프", "리겔"],
    [[0.74, 0.12], [0.26, 0.16], [0.62, 0.48], [0.50, 0.51], [0.38, 0.54], [0.78, 0.90], [0.22, 0.86]],
    [[0, 1], [0, 2], [1, 4], [2, 3], [3, 4], [2, 5], [4, 6]]),
];

export const CONSTELLATIONS = [...ZODIAC, ...OTHERS];

/** "2026.09.04" 같은 표시용 날짜를 정렬 가능한 문자열로 */
const key = (d) => String(d ?? "");

/**
 * 별자리를 채울 책. 최근에 읽은 것부터 쓴다 — 지금의 내가 보이고,
 * 다음에 한 권을 더 읽으면 그림이 바뀌어 다시 볼 이유가 생긴다.
 */
export function completedNewestFirst(books, uid) {
  return (books || [])
    .filter((b) => b.completedBy?.[uid])
    .map((b) => ({ id: b.id, title: b.title, author: b.author, done: b.completedBy[uid] }))
    .sort((a, b) => key(b.done).localeCompare(key(a.done)));
}

/* ── 고른 책 기억하기 ─────────────────────────────────────
   기본은 최근에 읽은 N권이다. 손으로 고른 것은 기기에 남긴다 —
   아홉 권을 골라 놓고 다시 열었을 때 처음으로 돌아가 있으면
   고르는 기능이 없는 것보다 나쁘다.

   DB 에 두지 않는다. 이 그림은 내보내기 위한 것이고 남이 볼 값이
   아니어서, 서버까지 갈 이유가 없다. 브라우저가 막아둔 환경에서도
   기본값으로 그림이 나오면 되므로 실패는 조용히 넘긴다. */
const PICK_KEY = "muroo.constellationPicks.v1";

function readAll() {
  try {
    return JSON.parse(localStorage.getItem(PICK_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

/** @returns {number[]|null} 고른 책 id, 없으면 null */
export function loadPick(uid, key) {
  if (!uid) return null;
  const all = readAll();
  const ids = all?.[uid]?.[key];
  return Array.isArray(ids) && ids.length ? ids : null;
}

export function savePick(uid, key, ids) {
  if (!uid) return;
  try {
    const all = readAll();
    all[uid] = { ...(all[uid] || {}), [key]: ids };
    localStorage.setItem(PICK_KEY, JSON.stringify(all));
  } catch {
    /* 저장이 막혀도 이번 화면에는 반영되어 있다 */
  }
}

export function clearPick(uid, key) {
  if (!uid) return;
  try {
    const all = readAll();
    if (all[uid]) {
      delete all[uid][key];
      localStorage.setItem(PICK_KEY, JSON.stringify(all));
    }
  } catch {
    /* 무시 */
  }
}

/**
 * 지금 무엇을 만들 수 있는지.
 * @returns {{tpl, have, ready, left, fill, custom}[]}
 */
export function collectConstellations(books, uid) {
  const done = completedNewestFirst(books, uid);
  const byId = new Map(done.map((b) => [b.id, b]));

  return CONSTELLATIONS.map((tpl) => {
    /* 고른 책 중에 지워지거나 완독이 풀린 것이 있으면 개수가 안 맞는다.
       그때는 손으로 고른 적이 없는 것처럼 최근 순서로 되돌린다 —
       빈 별을 남기면 그림이 깨진 것처럼 보인다. */
    const saved = loadPick(uid, tpl.key);
    const picked = saved ? saved.map((id) => byId.get(id)).filter(Boolean) : null;
    const custom = !!picked && picked.length === tpl.need;

    return {
      tpl,
      have: done.length,
      ready: done.length >= tpl.need,
      left: Math.max(0, tpl.need - done.length),
      fill: custom ? picked : done.slice(0, tpl.need),
      custom,
    };
  });
}
